"use client";

import "twin.macro";

import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useCreateClient } from "@/api/clients/mutation";
import Error from "@/components/error";
import Field from "@/components/field";
import Input from "@/components/inputs";
import Label from "@/components/label";

interface ClientFormProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface CreateClientValues {
  // type: string;
  name: string;
  // email: string;
  tel: string;
  address: string;
  city: string;
  zipcode: string;
  region?: string;
}

export const ClientForm = ({ open, setOpen }: ClientFormProps) => {
  const { t } = useTranslation();
  const createClient = useCreateClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitSuccessful },
  } = useForm<CreateClientValues>({
    defaultValues: {
      // type: "",
      name: "",
      // email: "",
      tel: "",
      address: "",
      city: "",
      zipcode: "",
      region: "",
    },
  });

  const onSubmit: SubmitHandler<CreateClientValues> = async (data) => {
    try {
      await createClient.mutateAsync({
        // type: data.type,
        name: data.name,
        // email: data.email,
        tel: data.tel,
        address: data.address,
        city: data.city,
        zipcode: data.zipcode,
        region: data.region || "",
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setOpen(false);
    reset();
  }, [isSubmitSuccessful]);

  return (
    <Dialog open={open} onClose={setOpen} tw="relative z-10">
      <DialogBackdrop
        transition
        tw="fixed inset-0 bg-gray-500/75 transition-opacity duration-500 ease-in-out data-[closed]:opacity-0"
      />

      <div tw="fixed inset-0 overflow-hidden">
        <div tw="absolute inset-0 overflow-hidden">
          <div tw="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel
              transition
              tw="pointer-events-auto relative w-screen max-w-md transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
            >
              <div tw="flex h-full flex-col bg-white py-6 shadow-xl overflow-auto">
                <div tw="px-4 sm:px-6">
                  <div tw="flex items-start justify-between">
                    <DialogTitle tw="mt-6 text-lg font-semibold text-gray-900">
                      {t("form.addClient.title")}
                    </DialogTitle>
                    <div tw="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        onClick={() => setOpen(false)}
                        tw="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                      >
                        <span tw="absolute -inset-2.5" />
                        <span tw="sr-only">{t("form.close")}</span>
                        <XMarkIcon aria-hidden="true" tw="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                </div>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div tw="flex flex-col mt-8 gap-6 px-4 sm:px-6">
                    {/* 
                    //todo add type of client
                    <Field tw="flex-1">
                      <Label>{t("form.addClient.type")}</Label>
                      <Select {...register("type")}>
                        <TypeOptions />
                      </Select>
                    </Field> */}
                    <Field tw="flex-1">
                      <Label>{t("form.addClient.name")}</Label>
                      <Input
                        {...register("name", { required: true })}
                        invalid={Boolean(errors.name)}
                      />
                      {errors.name?.type === "required" && (
                        <Error>{t("form.mandatory")}</Error>
                      )}
                    </Field>
                    <Field tw="flex-1">
                      <Label>{t("form.addClient.tel")}</Label>
                      <Input
                        {...register("tel", { required: true })}
                        invalid={Boolean(errors.tel)}
                        autoComplete="off"
                      />
                      {errors.region?.type === "required" && (
                        <Error>{t("form.mandatory")}</Error>
                      )}
                    </Field>
                    <Field tw="flex-1">
                      <Label>{t("form.addClient.address")}</Label>
                      <Input
                        {...register("address", { required: true })}
                        invalid={Boolean(errors.address)}
                      />
                      {errors.address?.type === "required" && (
                        <Error>{t("form.mandatory")}</Error>
                      )}
                    </Field>
                    <div tw="flex gap-4">
                      <Field tw="flex-1">
                        <Label>{t("form.addClient.city")}</Label>
                        <Input
                          {...register("city", { required: true })}
                          invalid={Boolean(errors.city)}
                        />
                        {errors.city?.type === "required" && (
                          <Error>{t("form.mandatory")}</Error>
                        )}
                      </Field>
                      <Field tw="flex-1">
                        <Label>{t("form.addClient.zipCode")}</Label>
                        <Input
                          {...register("zipcode", { required: true })}
                          invalid={Boolean(errors.zipcode)}
                        />
                        {errors.zipcode?.type === "required" && (
                          <Error>{t("form.mandatory")}</Error>
                        )}
                      </Field>
                    </div>

                    <Field tw="flex-1">
                      <Label>{t("form.addClient.region")}</Label>
                      <Input {...register("region")} />
                    </Field>
                  </div>
                  <div tw="flex mt-8 shrink-0 justify-end px-4 py-4">
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      tw="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
                    >
                      {t("global.cancel")}
                    </button>
                    <button
                      type="submit"
                      tw="ml-4 inline-flex justify-center rounded-md bg-primary-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-800"
                    >
                      {t("global.save")}
                    </button>
                  </div>
                </form>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
